import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { buildQueryParams } from "@finitless/shared/lib";

// Fields requested for each promotion from Ordering API
const PROMOTIONS_PARAMS =
  "name,auto,enabled,end,description,image,label,order_priority,sites,stackable,start,target,type,limit_per_user,user_order_count,user_order_count_condition,valid_from_after_user_last_order_minutes,valid_until_after_user_last_order_minutes,users,delivery_zones,paymethods,order_types_allowed,max_discount,rank,rate_type,rate,public,coupon,businesses,condition_type,minimum,products,categories,schedule,limit,include_options,loyalty_levels";

/**
 * Hook to fetch promotions (offers) from Ordering API.
 *
 * Uses the app's configured project/URL, so it will hit:
 *   {baseUrl}/offers?page=...&page_size=...&params=...
 *
 * @param {Object} options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.pageSize - Page size (default: 10)
 * @param {string} options.searchTerm - Search term to filter by name/description
 * @param {'ALL'|'ACTIVE'|'DISABLED'} options.statusFilter - Filter by enabled flag (default: 'ALL')
 * @param {boolean} options.enabled - Whether the query is enabled (default: true)
 * @returns {{ promotions: Array, pagination: Object, ...reactQueryResult }}
 */
export const usePromotions = ({
  page = 1,
  pageSize = 10,
  searchTerm = "",
  statusFilter = "ALL",
  enabled = true,
} = {}) => {
  const trimmedSearch = searchTerm?.trim() || "";

  const queryParams = {
    page,
    page_size: pageSize,
    params: PROMOTIONS_PARAMS,
  };

  const where = {
    conditions: [],
    conector: "AND",
  };

  if (trimmedSearch) {
    where.conditions.push({
      conector: "OR",
      conditions: [
        {
          attribute: "name",
          value: {
            condition: "ilike",
            value: `%${trimmedSearch}%`,
          },
        },
        {
          attribute: "description",
          value: {
            condition: "ilike",
            value: `%${trimmedSearch}%`,
          },
        },
      ],
    });
  }

  if (statusFilter === "ACTIVE") {
    where.conditions.push({
      attribute: "enabled",
      value: true,
    });
  } else if (statusFilter === "DISABLED") {
    where.conditions.push({
      attribute: "enabled",
      value: false,
    });
  }

  if (where.conditions.length > 0) {
    queryParams.where = where;
  }

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["promotions", page, pageSize, trimmedSearch, statusFilter],
    enabled,
    queryFn: async () => {
      const response = await apiClient.get(
        `/offers${queryString ? `?${queryString}` : ""}`,
      );

      return {
        promotions: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
};

/**
 * Hook to fetch a single promotion (offer) by id.
 *
 * @param {Object} options
 * @param {number|string} options.id - Promotion ID
 * @param {boolean} options.enabled - Whether the query is enabled (default: true)
 * @returns {{ promotion: Object|null, ...reactQueryResult }}
 */
export const usePromotionById = ({ id, enabled = true } = {}) => {
  return useQuery({
    queryKey: ["promotion", id],
    enabled: !!id && enabled,
    queryFn: async () => {
      const response = await apiClient.get(`/offers/${id}`, {
        params: {
          params: PROMOTIONS_PARAMS,
        },
      });

      return response?.result || null;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
};

export const usePromotionPaymethods = () => {
  return useQuery({
    queryKey: ["promotion-paymethods"],
    queryFn: async () => {
      const response = await apiClient.get("/paymethods");

      return response?.result || [];
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
};

export const usePromotionDeliveryZones = ({
  page = 1,
  pageSize = 100,
  businessIds = null,
  enabled = true,
} = {}) => {
  const normalizedBusinessIds = Array.isArray(businessIds)
    ? businessIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id))
        .sort((a, b) => a - b)
    : [];

  const paramsFields = "name,minimum,price,type,data,enabled,business_id";

  const where = {
    conditions: [
      {
        attribute: "type",
        value: {
          condition: "!=",
          value: 3,
        },
      },
      {
        attribute: "businesses",
        conditions: [
          {
            attribute: "business_id",
            value: normalizedBusinessIds,
          },
        ],
      },
    ],
    conector: "AND",
  };

  const queryStringParts = [
    `page=${encodeURIComponent(String(page))}`,
    `page_size=${encodeURIComponent(String(pageSize))}`,
    `params=${encodeURIComponent(paramsFields)}`,
  ];

  // Only apply business filtering when we have selected businesses.
  if (normalizedBusinessIds.length > 0) {
    queryStringParts.push(
      `filtered_by_business=${encodeURIComponent(
        JSON.stringify(normalizedBusinessIds),
      )}`,
    );
    queryStringParts.push(`where=${encodeURIComponent(JSON.stringify(where))}`);
  }

  const queryString = queryStringParts.join("&");

  return useQuery({
    queryKey: [
      "promotion-delivery-zones",
      page,
      pageSize,
      normalizedBusinessIds.join(","),
    ],
    enabled: enabled && normalizedBusinessIds.length > 0,
    queryFn: async () => {
      const response = await apiClient.get(
        `/delivery_zones${queryString ? `?${queryString}` : ""}`,
      );

      return {
        zones: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
};

/**
 * Hook to toggle promotion enabled flag
 * Sends { enabled } to /offers/:id and then invalidates promotions queries.
 */
export const useTogglePromotionEnabled = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, enabled }) => {
      const response = await apiClient.post(`/offers/${id}`, { enabled });
      return response?.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promotions"], exact: false });
    },
  });
};

/**
 * Hook to delete a promotion (offer) by id
 */
export const useDeletePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }) => {
      const response = await apiClient.delete(`/offers/${id}`);
      return response?.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["promotions"], exact: false });
    },
  });
};

export default {
  usePromotions,
  usePromotionById,
  useTogglePromotionEnabled,
  useDeletePromotion,
  usePromotionPaymethods,
  usePromotionDeliveryZones,
};
